import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Icons } from '../shared/ui';
const { Database: CircleStackIcon, Cog: CogIcon, Shield: ShieldCheckIcon, Refresh: ArrowPathIcon, CheckCircle2: CheckCircleIcon, Alert: ExclamationTriangleIcon, Lock: KeyIcon, Sparkles: SparklesIcon, Eye: EyeIcon, EyeOff: EyeSlashIcon } = Icons;
const ServerIcon = Icons.Database;
import api from '../utils/api';
import toast from 'react-hot-toast';

const SystemConfig = () => {
  const { token } = useAuth();
  const [activeSection, setActiveSection] = useState('general');
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // AI Services state
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [geminiModelName, setGeminiModelName] = useState('gemini-2.0-flash');
  const [showApiKey, setShowApiKey] = useState(false);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [testingGemini, setTestingGemini] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);

  const sections = [
    { id: 'general', name: 'General Settings', icon: CogIcon },
    { id: 'database', name: 'Database Config', icon: CircleStackIcon },
    { id: 'security', name: 'Security Settings', icon: ShieldCheckIcon },
    { id: 'services', name: 'Service Endpoints', icon: ServerIcon },
    { id: 'ai', name: 'AI Services (OCR)', icon: SparklesIcon },
    { id: 'vault', name: 'Vault Secrets', icon: KeyIcon },
  ];

  // Fetch configurations from Consul
  useEffect(() => {
    fetchConfigs();
  }, []);

  // Fetch OCR status when AI section is active
  useEffect(() => {
    if (activeSection === 'ai') {
      fetchOcrStatus();
    }
  }, [activeSection]);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/config/consul');
      setConfigs(response.data);
      toast.success('Configuration loaded from Consul');
    } catch (error) {
      console.error('Failed to fetch configs:', error);
      toast.error('Failed to load configuration');
      // Fallback to default configs
      setConfigs(getDefaultConfigs());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultConfigs = () => ({
    general: {
      app_name: 'Apartment Management System',
      timezone: 'Asia/Ho_Chi_Minh',
      language: 'vi',
      maintenance_mode: false,
      max_upload_size_mb: 10,
      session_timeout_minutes: 60,
    },
    database: {
      postgres_pool_size: 20,
      postgres_timeout: 30,
      mongodb_pool_size: 10,
      mongodb_timeout: 20,
      enable_query_logging: false,
    },
    security: {
      jwt_expiry_hours: 24,
      password_min_length: 8,
      require_2fa: false,
      max_login_attempts: 5,
      lockout_duration_minutes: 30,
      enable_mtls: true,
      vpn_required: true,
    },
    services: {
      ocr_timeout: 60,
      lpr_timeout: 30,
      person_detection_timeout: 30,
      rabbitmq_prefetch_count: 10,
      edge_sync_interval_seconds: 60,
      notification_retry_attempts: 3,
    },
    vault: {
      auto_unseal: true,
      seal_type: 'shamir',
      key_shares: 5,
      key_threshold: 3,
      max_lease_ttl: '768h',
    }
  });

  const saveConfig = async (section, key, value) => {
    setSaving(true);
    try {
      await api.put(`/api/config/consul/${section}/${key}`, { value });

      // Update local state
      setConfigs(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value
        }
      }));

      toast.success(`Configuration updated: ${key}`);
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const syncWithVault = async () => {
    try {
      const response = await api.post('/api/config/sync-vault', {});
      toast.success('Secrets synchronized with Vault');
    } catch (error) {
      console.error('Vault sync failed:', error);
      toast.error('Failed to sync with Vault');
    }
  };

  // Fetch OCR service status
  const fetchOcrStatus = async () => {
    try {
      const response = await api.get('/api/ocr/config/status');
      setOcrStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch OCR status:', error);
      setOcrStatus({ gemini_configured: false, error: true });
    }
  };

  // Save Gemini API key and model
  const saveGeminiApiKey = async () => {
    if (!geminiApiKey || geminiApiKey.length < 10) {
      toast.error('Please enter a valid API key');
      return;
    }

    if (!geminiModelName || geminiModelName.length < 3) {
      toast.error('Please enter a valid model name');
      return;
    }

    setSavingApiKey(true);
    try {
      const formData = new FormData();
      formData.append('api_key', geminiApiKey);
      formData.append('model_name', geminiModelName);

      const response = await api.post('/api/ocr/config/gemini-api-key', formData);
      toast.success(response.data.message || 'Gemini API configuration saved successfully');
      setGeminiApiKey('');
      setEditingConfig(false);
      await fetchOcrStatus();
    } catch (error) {
      console.error('Failed to save API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to save API configuration');
    } finally {
      setSavingApiKey(false);
    }
  };

  // Test Gemini connection
  const testGeminiConnection = async () => {
    setTestingGemini(true);
    try {
      const response = await api.post('/api/ocr/config/test');
      toast.success(`Gemini API working! Model: ${response.data.model}, Response: "${response.data.response}"`);
    } catch (error) {
      console.error('Gemini test failed:', error);
      toast.error(error.response?.data?.detail || 'Gemini API test failed');
    } finally {
      setTestingGemini(false);
    }
  };

  // Delete Gemini configuration
  const deleteGeminiConfig = async () => {
    if (!window.confirm('Are you sure you want to delete the Gemini API configuration? This will remove the API key and model from Vault.')) {
      return;
    }

    setDeletingConfig(true);
    try {
      await api.delete('/api/ocr/config/gemini');
      toast.success('Gemini configuration deleted successfully');
      setEditingConfig(false);
      setGeminiApiKey('');
      setGeminiModelName('gemini-2.0-flash');
      fetchOcrStatus();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setDeletingConfig(false);
    }
  };

  // Fetch OCR status when AI section is active
  useEffect(() => {
    if (activeSection === 'ai') {
      fetchOcrStatus();
    }
  }, [activeSection]);

  const renderConfigField = (section, key, value) => {
    const inputType = typeof value === 'boolean' ? 'checkbox' : 
                     typeof value === 'number' ? 'number' : 'text';

    if (typeof value === 'boolean') {
      return (
        <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
          <label className="flex items-center cursor-pointer">
            <span className="text-sm font-medium text-stone-700">
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </label>
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => saveConfig(section, key, e.target.checked)}
            size={16} className="text-teal-600 focus:ring-teal-500 border-stone-300 rounded"
            disabled={saving}
          />
        </div>
      );
    }

    return (
      <div className="p-3 border rounded-lg hover:bg-stone-50">
        <label className="block text-sm font-medium text-stone-700 mb-1">
          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </label>
        <input
          type={inputType}
          value={value}
          onChange={(e) => {
            const newValue = inputType === 'number' ? 
              parseInt(e.target.value) : e.target.value;
            saveConfig(section, key, newValue);
          }}
          className="w-full px-3 py-1 border border-stone-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
          disabled={saving}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon size={32} className="animate-spin text-teal-600" />
        <span className="ml-2">Loading configurations...</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-stone-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-stone-900">
            System Configuration
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={syncWithVault}
              className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              disabled={saving}
            >
              <KeyIcon size={16} className="mr-2" />
              Sync with Vault
            </button>
            <button
              onClick={fetchConfigs}
              className="flex items-center px-3 py-2 bg-stone-600 text-white rounded-md hover:bg-stone-700"
              disabled={loading}
            >
              <ArrowPathIcon size={16} className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4">
        {/* Sidebar */}
        <div className="md:col-span-1 p-4 border-r border-stone-200">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeSection === section.id
                    ? 'bg-teal-50 text-teal-700'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`}
              >
                <section.icon size={20} className="mr-3" />
                {section.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="md:col-span-3 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-stone-900 mb-1">
              {sections.find(s => s.id === activeSection)?.name}
            </h3>
            <p className="text-sm text-stone-500">
              {activeSection === 'ai'
                ? 'Configure AI services including OCR with Google Gemini.'
                : `Configure ${activeSection} settings. Changes are saved automatically to Consul.`}
            </p>
          </div>

          {/* AI Services Section */}
          {activeSection === 'ai' ? (
            <div className="space-y-6">
              {/* OCR Status */}
              <div className="bg-stone-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-stone-700 mb-3">OCR Service Status</h4>
                {ocrStatus ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      {ocrStatus.gemini_configured ? (
                        <CheckCircleIcon size={20} className="text-green-600 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon size={20} className="text-yellow-600 mr-2" />
                      )}
                      <span className="text-sm">
                        Gemini API: {ocrStatus.gemini_configured ? 'Configured' : 'Not Configured'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {ocrStatus.vault_connected ? (
                        <CheckCircleIcon size={20} className="text-green-600 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon size={20} className="text-yellow-600 mr-2" />
                      )}
                      <span className="text-sm">
                        Vault: {ocrStatus.vault_connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {ocrStatus.mongodb_connected ? (
                        <CheckCircleIcon size={20} className="text-green-600 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon size={20} className="text-red-600 mr-2" />
                      )}
                      <span className="text-sm">
                        MongoDB: {ocrStatus.mongodb_connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {ocrStatus.postgresql_connected ? (
                        <CheckCircleIcon size={20} className="text-green-600 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon size={20} className="text-red-600 mr-2" />
                      )}
                      <span className="text-sm">
                        PostgreSQL: {ocrStatus.postgresql_connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center text-stone-500">
                    <ArrowPathIcon size={16} className="animate-spin mr-2" />
                    Loading status...
                  </div>
                )}
                <button
                  onClick={fetchOcrStatus}
                  className="mt-3 text-sm text-teal-600 hover:text-teal-800 flex items-center"
                >
                  <ArrowPathIcon size={16} className="mr-1" />
                  Refresh Status
                </button>
              </div>

              {/* Gemini API Configuration */}
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center">
                  <SparklesIcon size={20} className="mr-2 text-purple-600" />
                  Google Gemini API Configuration
                </h4>

                {/* Show current config if configured and not editing */}
                {ocrStatus?.gemini_configured && !editingConfig ? (
                  <div className="space-y-3">
                    {/* Current Configuration Display */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <CheckCircleIcon size={20} className="text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-800">Configured</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-stone-600">Model:</span>
                          <span className="font-mono text-stone-800">{ocrStatus.gemini_model}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">API Key:</span>
                          <span className="font-mono text-stone-800">{ocrStatus.gemini_api_key_masked}</span>
                        </div>
                      </div>
                    </div>

                    {/* Edit/Delete Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingConfig(true)}
                        className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 flex items-center justify-center"
                      >
                        <CogIcon size={16} className="mr-2" />
                        Edit Configuration
                      </button>
                      <button
                        onClick={deleteGeminiConfig}
                        disabled={deletingConfig}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                      >
                        {deletingConfig ? (
                          <ArrowPathIcon size={16} className="animate-spin" />
                        ) : (
                          <ExclamationTriangleIcon size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Show form when not configured or editing */
                  <div className="space-y-3">
                    <p className="text-xs text-stone-500">
                      Get your API key from{' '}
                      <a
                        href="https://aistudio.google.com/app/apikey"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:underline"
                      >
                        Google AI Studio
                      </a>
                      . The key and model will be stored securely in Vault.
                    </p>

                    {/* API Key Input */}
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">API Key</label>
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={geminiApiKey}
                          onChange={(e) => setGeminiApiKey(e.target.value)}
                          placeholder="Enter Gemini API key..."
                          className="w-full px-3 py-2 pr-10 border border-stone-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        >
                          {showApiKey ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Model Name Input */}
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">Model Name</label>
                      <input
                        type="text"
                        value={geminiModelName}
                        onChange={(e) => setGeminiModelName(e.target.value)}
                        placeholder="e.g., gemini-2.0-flash, gemini-1.5-pro"
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="text-xs text-stone-400 mt-1">
                        Examples: gemini-2.0-flash, gemini-2.0-flash-lite, gemini-1.5-pro, gemini-1.5-flash
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={saveGeminiApiKey}
                        disabled={savingApiKey || !geminiApiKey || !geminiModelName}
                        className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {savingApiKey ? (
                          <>
                            <ArrowPathIcon size={16} className="animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <KeyIcon size={16} className="mr-2" />
                            Save to Vault
                          </>
                        )}
                      </button>
                      {editingConfig && (
                        <button
                          onClick={() => {
                            setEditingConfig(false);
                            setGeminiApiKey('');
                          }}
                          className="px-4 py-2 bg-stone-300 text-stone-700 rounded-md hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Test Gemini Connection */}
              <div className="bg-white border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-stone-700 mb-3">Test Connection</h4>
                <p className="text-xs text-stone-500 mb-3">
                  Send a test request to verify the Gemini API is working correctly.
                </p>
                <button
                  onClick={testGeminiConnection}
                  disabled={testingGemini || !ocrStatus?.gemini_configured}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {testingGemini ? (
                    <>
                      <ArrowPathIcon size={16} className="animate-spin mr-2" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <SparklesIcon size={16} className="mr-2" />
                      Test Gemini API
                    </>
                  )}
                </button>
                {!ocrStatus?.gemini_configured && (
                  <p className="mt-2 text-xs text-yellow-600">
                    Configure the Gemini API key first to test the connection.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {configs[activeSection] && Object.entries(configs[activeSection]).map(([key, value]) => (
                  <div key={key}>
                    {renderConfigField(activeSection, key, value)}
                  </div>
                ))}
              </div>

              {/* Status Indicator */}
              {saving && (
                <div className="mt-4 flex items-center text-sm text-stone-500">
                  <ArrowPathIcon size={16} className="animate-spin mr-2" />
                  Saving configuration...
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Service Health Status */}
      <div className="px-6 py-4 border-t border-stone-200 bg-stone-50">
        <h3 className="text-sm font-medium text-stone-900 mb-3">Service Health</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ServiceHealth name="Consul" status="healthy" />
          <ServiceHealth name="Vault" status="healthy" />
          <ServiceHealth name="RabbitMQ" status="healthy" />
          <ServiceHealth name="Edge Sync" status="warning" />
        </div>
      </div>
    </div>
  );
};

const ServiceHealth = ({ name, status }) => {
  const statusColors = {
    healthy: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600',
  };

  const StatusIcon = status === 'healthy' ? CheckCircleIcon : 
                     status === 'warning' ? ExclamationTriangleIcon : 
                     ExclamationTriangleIcon;

  return (
    <div className="flex items-center">
      <StatusIcon size={20} className={`${statusColors[status]} mr-2`} />
      <span className="text-sm text-stone-700">{name}</span>
    </div>
  );
};

export default SystemConfig;
